import React, { useState } from 'react';
import { Waves, Wind, ArrowUp, Clock, ChevronUp, ChevronDown, Thermometer } from 'lucide-react';
import { motion } from 'framer-motion';
import { SHAPE_LABELS, getShapeLabel, formatHour, DataSourceBadge, LoadingDots } from './constants';

export default function SpotSpitcastTab({ spitcast, spitcastLoaded, tabLoading }) {
  const [showAll, setShowAll] = useState(false);

  if (tabLoading.spitcast && !spitcastLoaded) {
    return <LoadingDots />;
  }

  if (!spitcastLoaded) {
    return <p className="text-xs text-muted-foreground/50 text-center py-8">No Spitcast data available for this spot</p>;
  }

  const currentHr = new Date().getHours();
  const peak = spitcast.hours.reduce((best, h) => (!best || h.size_ft > best.size_ft) ? h : best, null);
  const visible = showAll ? spitcast.hours : spitcast.hours.filter(h => h.hour >= currentHr - 1).slice(0, 12);

  return (
    <div className="space-y-4" data-testid="spot-spitcast">
      {/* Summary cards */}
      <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Waves className="w-3.5 h-3.5 text-primary/50" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Spitcast Forecast</span>
          <DataSourceBadge label="Spitcast" active />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {peak && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
              <ArrowUp className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
              <p className="text-sm font-bold">{peak.size_ft}<span className="text-[9px] text-muted-foreground/50">ft</span></p>
              <p className="text-[9px] text-muted-foreground/50">peak @ {formatHour(peak.hour)}</p>
            </div>
          )}
          {peak && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
              <Wind className="w-3.5 h-3.5 text-yellow-400 mx-auto mb-1" />
              <p className={`text-xs font-bold ${getShapeLabel(peak.shape).color.split(' ')[0]}`}>{getShapeLabel(peak.shape).label}</p>
              <p className="text-[9px] text-muted-foreground/50">shape quality</p>
            </div>
          )}
          {spitcast.buoy?.water_temp_f && (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-2.5 text-center">
              <Thermometer className="w-3.5 h-3.5 text-cyan-400 mx-auto mb-1" />
              <p className="text-sm font-bold">{spitcast.buoy.water_temp_f}<span className="text-[9px] text-muted-foreground/50">&deg;F</span></p>
              <p className="text-[9px] text-muted-foreground/50">water temp</p>
            </div>
          )}
        </div>
      </div>

      {/* Hourly forecast bars */}
      <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4" data-testid="spitcast-hourly">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-primary/50" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-semibold">Hourly Forecast</span>
          </div>
          <button data-testid="toggle-spitcast-hours" onClick={() => setShowAll(!showAll)}
            className="text-[10px] text-primary/60 hover:text-primary flex items-center gap-0.5">
            {showAll ? 'current' : 'all 24h'}
            {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        <div className="space-y-0.5">
          {visible.map(h => {
            const shapeInfo = getShapeLabel(h.shape);
            const barWidth = Math.max(8, Math.min(100, (h.size_ft / 12) * 100));
            return (
              <div key={h.hour} className="flex items-center gap-3 py-1.5" data-testid={`spitcast-hour-${h.hour}`}>
                <span className="text-[11px] text-muted-foreground/60 w-10 flex-shrink-0 font-mono">{formatHour(h.hour)}</span>
                <div className="flex-1 h-5 bg-white/[0.03] rounded-full overflow-hidden relative">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${barWidth}%` }} transition={{ duration: 0.4, delay: h.hour * 0.02 }}
                    className={`h-full rounded-full ${h.shape >= 0.7 ? 'bg-primary/40' : h.shape >= 0.5 ? 'bg-yellow-400/30' : 'bg-red-400/20'}`} />
                </div>
                <span className="text-xs font-bold w-12 text-right flex-shrink-0">{h.size_ft}<span className="text-[9px] text-muted-foreground/50 ml-0.5">ft</span></span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 w-16 text-center ${shapeInfo.color}`}>{shapeInfo.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Shape legend */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
        <p className="text-[9px] text-muted-foreground/40 mb-2">Shape Quality Legend</p>
        <div className="flex flex-wrap gap-1.5">
          {SHAPE_LABELS.map(s => (
            <span key={s.label} className={`text-[9px] px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
